import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslate } from 'nucleus-text';
import useSessionStatus from '@tools/sessionUtils/useSessionStatus';
import { Event_Info_PageQuery as EventInfoPageQuery } from '@graphql/gql/graphql';
import useInterval from '@hooks/useInterval';
import getSessionStatus from '@tools/sessionUtils/SessionStatus';
import JoinSessionAction from '@components/sessionDetails/JoinSessionAction';
import { GO_BACK_KEYS, setSessionStorageData, getGoBackData } from '@tools/goBack';
import useMeQuery from '@hooks/useMeQuery';
import useAuthorization from '@hooks/useAuthorization';
import isFeatureEnabled from '@tools/isFeatureEnabled';
import {
  hasSessionStartedOrEnded,
  extractJoinSessionActionProps,
  shouldAutoPlay,
  hasOnDemandContentAvailable,
  hasDescriptionText
} from '@components/landing/landingUtils';
import {
  FEATURE_SPEED_NETWORKING,
  FEATURE_VIDEO_DISCUSSIONS,
  FEATURE_MESSAGING,
  CVENT_BRANDING
} from '@components/engagement/constants';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useSessionInfo from '@components/sessionDetails/useSessionInfo';
import { usePageActions, usePageMetrics } from '@cvent/sli-nextjs-metrics';
import LoadingContent from '@components/shared/LoadingContent';
import ErrorState from '@components/shared/ErrorState';
import getConfig from 'next/config';
import SessionBanner from '@components/sessionDetails/SessionBanner';
import InfoPageCallToAction from './InfoPageCallToAction';
import AttendeesMeet from './featuredSection/AttendeesMeet';
import InfoPageContent from './InfoPageContent';
import CventBrandingBlock from './featuredSection/CventBrandingBlock';

const { publicRuntimeConfig } = getConfig();

type InfoPageProps = {
  eventInfo: EventInfoPageQuery['eventInfo'];
  eventId?: string;
  appFeatures: Record<string, boolean>;
};

/**
 * Attendee Hub Info Page
 * (post-login)
 */

function InfoPage({ eventInfo }: InfoPageProps): JSX.Element {
  const { features, id: eventId, sessions, speakers } = eventInfo;

  const [now, setNow] = useState(new Date());
  useInterval(() => {
    setNow(new Date());
  }, 15000);

  const { trackAction, updateMetricData } = usePageActions();

  // Update the current metric data with the event info
  updateMetricData({
    numberOfSpeakers: speakers?.length
  });

  const router = useRouter();
  const { id: sessionId, start: sessionStart, end: sessionEnd } = sessions[0];
  const { translate } = useTranslate();

  const { sessionDetailsResponse, getIsLive } = useSessionInfo({
    sessionId,
    fetchPolicy: 'cache-and-network'
  });
  const {
    data: sessionDetailsData,
    loading: sessionDetailsLoading,
    error: sessionDetailsError
  } = sessionDetailsResponse;

  const isLive = getIsLive();
  const { webcast, virtual, enrolled, included, name } = sessionDetailsData?.sessionDetail || {};
  const sessionStatusFromNow = getSessionStatus(now, sessionStart, sessionEnd);
  const statusOnLoad = useRef(sessionStatusFromNow).current;

  const { isAttendee } = useAuthorization();
  const { data: userData } = useMeQuery();

  const { isContentPresent, isVirtualRoomEmbedded, onDemandVideo, streamStatus, vendor, webcastFormat } =
    extractJoinSessionActionProps({
      webcast,
      virtual,
      sessionStatus: sessionStatusFromNow,
      statusOnLoad,
      isLive
    });

  const isEnrolledOrIncluded = enrolled || included;

  const hasOnDemandContent = hasOnDemandContentAvailable({
    onDemandVideo,
    sessionStatus: sessionStatusFromNow,
    isEnrolledOrIncluded,
    isContentPresent,
    isVirtualRoomEmbedded
  });

  const displayAttendeesMeetSection =
    isFeatureEnabled(features, FEATURE_SPEED_NETWORKING) ||
    isFeatureEnabled(features, FEATURE_VIDEO_DISCUSSIONS) ||
    isFeatureEnabled(features, FEATURE_MESSAGING);

  const isBrandingEnabled = isFeatureEnabled(features, CVENT_BRANDING);

  const sessionStatus = useSessionStatus({
    start: sessionStart,
    end: sessionEnd
  });

  const pageLoading = sessionDetailsLoading;
  const pageError = Boolean(sessionDetailsError);
  const errorMessage = pageError
    ? `Something went wrong while fetching data >>>>> ${{
        sessionDetailsError
      }}`
    : undefined;

  /**
   * Effect to track attendee landing on info page
   */
  useEffect(() => {
    if (pageLoading) return;
    trackAction('act_webinar_attendee_info', { destinations: ['Mixpanel'] });
  }, [trackAction, pageLoading]);
  // Track page load
  usePageMetrics({
    pageIsLoading: () => pageLoading,
    pageError: () => sessionDetailsError,
    data: { error: errorMessage, ssr: true }
  });

  if (pageError) {
    return <ErrorState />;
  }
  if (pageLoading) {
    return <LoadingContent />;
  }

  const isGlobalModerator = userData?.me?.permissions?.isGlobalModerator;
  const sessionsToModerate = userData?.me?.permissions?.sessionsToModerate || [];
  const baseSessionsURL = `/events/${eventId}/sessions/${sessionId}`;
  const buttonUrl = shouldAutoPlay({
    isGlobalModerator,
    sessionsToModerate,
    sessionId
  })
    ? `${baseSessionsURL}?autoPlay=true`
    : baseSessionsURL;

  const { GO_BACK_HREF, GO_BACK_NAME } = GO_BACK_KEYS;
  const { goBackName } = getGoBackData();

  const onClickEnterRoom = (): void => {
    setSessionStorageData({
      [GO_BACK_HREF]: router.asPath,
      [GO_BACK_NAME]: goBackName
    });

    router.push(buttonUrl);
  };

  const disableJoinButton = isAttendee(sessionId) && !hasSessionStartedOrEnded(sessionStatusFromNow);

  const viewFullDetailsOnClick = (): void => {
    setSessionStorageData({
      [GO_BACK_HREF]: router.asPath,
      [GO_BACK_NAME]: goBackName
    });
  };

  const hasDescription = hasDescriptionText(eventInfo);

  const coverImage = userData?.me?.event?.appConfig?.webConfig?.coverImage;
  const coverImageAltText = userData?.me?.event?.appConfig?.webConfig?.coverImageAltText;

  const viewSessionCallToAction = (
    <JoinSessionAction
      sessionId={sessionId}
      vendor={vendor}
      webcastFormat={webcastFormat}
      onDemandVideo={onDemandVideo}
      streamStatus={streamStatus}
      sessionStatus={sessionStatus}
      onClickEnterRoom={onClickEnterRoom}
      isEnrolledOrIncluded={isEnrolledOrIncluded}
      isContentPresent={isContentPresent}
      isVirtualRoomEmbedded={isVirtualRoomEmbedded}
      isLive={isLive}
      replaySessionText={translate('External-Video-Button-View-Recording')}
      showButtonIcon={false}
      defaultButtonContainerMargin={false}
      disableJoinButton={disableJoinButton}
      sessionName={name}
    />
  );

  const descriptionCallToAction = (
    <InfoPageCallToAction
      hasDescription={hasDescription}
      hasOnDemandContent={hasOnDemandContent}
      baseSessionsURL={baseSessionsURL}
      hasSessionStartedOrEnded={hasSessionStartedOrEnded(sessionStatus)}
      viewSessionCallToAction={viewSessionCallToAction}
      onClickViewFullDetails={viewFullDetailsOnClick}
      disableJoinButton={disableJoinButton}
    />
  );

  return (
    <div {...injectTestId('info-page-container')}>
      {coverImage && <SessionBanner imageSrc={coverImage} imageAltText={coverImageAltText} />}
      <InfoPageContent
        eventInfo={eventInfo}
        hasDescription={hasDescription}
        descriptionCallToAction={descriptionCallToAction}
        now={now}
      >
        {displayAttendeesMeetSection && <AttendeesMeet eventId={eventId} />}
        {isBrandingEnabled && (
          <CventBrandingBlock signupLink={`${publicRuntimeConfig.CVENT_WEBINAR_ACCOUNT_BASE_URL}/signup`} />
        )}
      </InfoPageContent>
    </div>
  );
}

export default InfoPage;
